import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';

import ErrorPage from 'components/ErrorPage/ErrorPage';
import { Skeleton } from '@material-ui/lab';
import { ErrorIcon } from 'evergreen-ui';
import {
    Container,
    HeaderBlock,
    InfoTextLeft,
    HeadingText,
    ContentContainer,
    Content,
} from './AddCompanyView.style';
import AddCompanyForm from 'views/AddCompanyView/AddCompanyForm/AddCompanyForm';
import { AuthenticatedUserProps } from '../../hoc/WithAuthLayout';
import {
    CreateCompanyInput,
    useConfirmUploadMutation,
    useCreateCompanyMutation,
    useGetUploadPresignedUrlMutation,
    useS3Upload,
} from '../../store/api/companies';

interface CreateCompanyViewProps extends AuthenticatedUserProps {}

const AddCompanyView: React.FC<CreateCompanyViewProps> = (props) => {
    const { userRoles } = props;
    const [createdCompanyName, setCreatedCompanyName] = useState<string>('');
    const navigate = useNavigate();

    const [
        getPresignedUrl,
        { isLoading: isGettingUploadUrl, error: gettingUploadUrlError },
    ] = useGetUploadPresignedUrlMutation();
    const {
        uploadToS3,
        isLoading: isUploadingLogo,
        error: uploadingLogoError,
    } = useS3Upload();
    const [
        confirmUpload,
        { isLoading: isConfirming, error: confirmingLogoUploadError },
    ] = useConfirmUploadMutation();
    const [
        createCompany,
        { isLoading: isCreating, error: creatingCompanyError, isSuccess },
    ] = useCreateCompanyMutation();

    const handleSubmit = async (createCompanyInput: CreateCompanyInput) => {
        const { data } = await getPresignedUrl({
            type: 'company-logo',
            file_name: 'logo.png',
            params: { company_slug: createCompanyInput.slug },
        });

        if (data && data.url) {
            const uploadSuccess = await uploadToS3(
                data.url,
                createCompanyInput.logo,
                'image/png',
            );

            if (uploadSuccess) {
                await confirmUpload(data.id);

                const logoUrl = data.url.split('?')[0];

                await createCompany({
                    name: createCompanyInput.name,
                    country: createCompanyInput.country || 'NL',
                    slug: createCompanyInput.slug,
                    logoUrl: logoUrl,
                });
            }
        }
    };

    const isLoading =
        isGettingUploadUrl || isUploadingLogo || isConfirming || isCreating;
    const error =
        gettingUploadUrlError ||
        uploadingLogoError ||
        confirmingLogoUploadError ||
        confirmingLogoUploadError ||
        creatingCompanyError;

    const renderTableInfoBox = () => {
        return (
            <HeaderBlock>
                <InfoTextLeft>
                    <HeadingText>
                        <h1>Add new client</h1>
                    </HeadingText>
                </InfoTextLeft>
            </HeaderBlock>
        );
    };

    const onCreateCompany = (companyName: string) => {
        setCreatedCompanyName(companyName);
    };

    // todo: move Skeleton and results container to AddCompanyForm component
    return (
        <Container>
            {userRoles.includes('super-admin') && (
                <React.Fragment>
                    {renderTableInfoBox()}
                    {isLoading && (
                        <ContentContainer>
                            <Skeleton
                                variant="text"
                                width="100%"
                                height="25px"
                                animation="wave"
                            />
                        </ContentContainer>
                    )}
                    {!isLoading && isSuccess && !error && createdCompanyName && (
                        <ContentContainer>
                            <Content>
                                New company {createdCompanyName} successfully
                                created. <br />
                                <br />
                                Click <Link to={'/'}>here</Link> to return to
                                the client list.
                            </Content>
                        </ContentContainer>
                    )}
                    {!isLoading && !!error && (
                        <ContentContainer>
                            <Content>Error: {error.toString()}</Content>
                        </ContentContainer>
                    )}
                    {!isLoading && !createdCompanyName && !error && (
                        <AddCompanyForm
                            cancelAddingCompany={() => navigate('/')}
                            onCreateCompany={onCreateCompany}
                            handleSubmit={handleSubmit}
                        />
                    )}
                </React.Fragment>
            )}
            {!userRoles.includes('super-admin') && (
                <ErrorPage
                    icon={ErrorIcon}
                    iconColor={'danger'}
                    message="You do not have permission to view this page."
                />
            )}
        </Container>
    );
};

export default AddCompanyView;
